import React, { useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { getDvaApp, useLocation } from 'umi';
import { Layout } from 'tea-component';
import componentMap from '@/pages/dashboard/_config/compConfig';
import { buriedPointGame, typeIdObj } from '@/pages/home/utils/buriedPoint';
import { StudioContext } from '@home/pages/studio/context';
import { FilterGameInfoContext } from '@op/components/FilterCommon/context';
import StudioQueryTabs from '../StudioQueryTabs';
import MarketingPageWrap from '../MarketingPageWrap';
import StudioTabPrNoticeMarquee from '../StudioTabPrNoticeMarquee';
import type {
  IInfluencerTabConfig,
  IInfluencerWrapperConfig,
} from './types';
import { STUDIO_INFLUENCER_FILTER_INSTANCE_ID } from './types';
import './index.scss';

const { Content } = Layout;
const OPINION_COMMON_NAMESPACE = 'opinionCommon';

const tabLogDedupe = { key: '', at: 0 };
const TAB_LOG_DEDUPE_MS = 800;

function ensureOpinionCommonModel(): void {
  const app = getDvaApp();
  if (!app) return;

  const hasModel = app._models?.some(
    (model: any) => model.namespace === OPINION_COMMON_NAMESPACE,
  );
  if (hasModel) return;

  app.model({
    namespace: OPINION_COMMON_NAMESPACE,
    state: {
      gameInfo: { edition_unified_id: '', entity_type: '', id_type: '' },
      showGameInfo: { edition_unified_id: '', entity_type: '', id_type: '' },
      listMenu: [],
      listMenuLoading: false,
      excludeSource: [],
      hidePageIds: [],
      wholeGameInfo: { edition_unified_id: '', entity_type: '', id_type: '' },
      changeGameVisible: false,
      pageLoading: false,
      gameInfoLoading: true,
      hideGameListMenu: false,
    },
    reducers: {
      save(state: any, { payload }: any) {
        const { wholeGameInfo } = payload || {};
        if (wholeGameInfo?.edition_unified_id) {
          localStorage.setItem('OpinionGameInfo', JSON.stringify(wholeGameInfo));
        }
        return { ...state, ...payload };
      },
    },
  });
}

function buildOpinionGameInfo(currentGame: any) {
  return {
    edition_unified_id:
      currentGame?.edition_unified_id || currentGame?.unified_edition_id,
    id_type: currentGame?.id_type,
    entity_type: currentGame?.entity_type || currentGame?.game_type,
  };
}

function buildOpinionWholeGameInfo(currentGame: any) {
  return {
    ...currentGame,
    ...buildOpinionGameInfo(currentGame),
    entity_image: currentGame?.entity_image || currentGame?.cover,
    entity_name:
      currentGame?.entity_name || currentGame?.game_name || currentGame?.game_name_cn,
    opinion_game: currentGame?.opinion_game ?? true,
    opinion_permissions: currentGame?.opinion_permissions ?? true,
  };
}

function syncOpinionCommonGameInfo(currentGame: any) {
  const opinionGameInfo = buildOpinionGameInfo(currentGame);
  const opinionWholeGameInfo = buildOpinionWholeGameInfo(currentGame);

  getDvaApp()?._store?.dispatch({
    type: `${OPINION_COMMON_NAMESPACE}/save`,
    payload: {
      gameInfo: opinionGameInfo,
      showGameInfo: opinionGameInfo,
      wholeGameInfo: opinionWholeGameInfo,
      gameInfoLoading: false,
      listMenuLoading: false,
      pageLoading: false,
    },
  });
}

function reportStudioTabLog(
  tab: IInfluencerTabConfig | undefined,
  gameName: string | undefined,
  gameId: string | undefined,
) {
  const logConfig = tab?.logConfig;
  const tabKey = tab?.key;
  if (!logConfig?.enabled || !gameId || !tabKey) {
    return;
  }
  const now = Date.now();
  if (tabLogDedupe.key === tabKey && now - tabLogDedupe.at < TAB_LOG_DEDUPE_MS) {
    return;
  }
  tabLogDedupe.key = tabKey;
  tabLogDedupe.at = now;
  buriedPointGame({
    gameName,
    pageId: logConfig.pageId || '',
    pageName: logConfig.pageName,
    extInfo: logConfig.extInfo,
    pagePath: logConfig.pagePath,
    typeId: typeIdObj.studio,
  }, gameId, 'unified_id');
}

function resolveActiveTabKey(
  tabs: IInfluencerTabConfig[],
  queryValue: string | null,
  defaultTab?: string,
) {
  const validKeys = new Set(tabs.map((tab) => tab.key));
  if (queryValue && validKeys.has(queryValue)) {
    return queryValue;
  }
  if (defaultTab && validKeys.has(defaultTab)) {
    return defaultTab;
  }
  return tabs[0]?.key;
}

function renderOpinionPage(tab: IInfluencerTabConfig) {
  const sourceComponent = componentMap[tab.name] as React.ComponentType<any> | undefined;
  if (!sourceComponent) {
    return null;
  }

  return React.createElement(sourceComponent, {
    ...tab.componentProps,
    filterInstanceId: STUDIO_INFLUENCER_FILTER_INSTANCE_ID,
    studioLogConfig: tab.logConfig,
  });
}

interface IInfluencerWrapperProps {
  config?: IInfluencerWrapperConfig;
}

const InfluencerWrapper: React.FC<IInfluencerWrapperProps> = ({ config }) => {
  const location = useLocation();
  const { currentGame } = useContext(StudioContext);
  const { unified_edition_id: gameId } = currentGame || {};
  const {
    tabs = [],
    queryKey = 'tab',
    defaultTab,
    titleEn,
    titleCn,
    descriptionEn,
    descriptionCn,
  } = config || {};

  ensureOpinionCommonModel();

  useEffect(() => {
    syncOpinionCommonGameInfo(currentGame);
  }, [currentGame]);

  const filterGameInfoContextValue = useMemo(
    () => {
      const opinionGameInfo = buildOpinionGameInfo(currentGame);
      const opinionWholeGameInfo = buildOpinionWholeGameInfo(currentGame);
      return {
        gameInfo: {
          ...opinionGameInfo,
          filterInstanceId: STUDIO_INFLUENCER_FILTER_INSTANCE_ID,
        },
        use: !!gameId,
        wholeGameInfo: opinionWholeGameInfo,
      };
    },
    [currentGame, gameId],
  );

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const activeKey = resolveActiveTabKey(
    tabs,
    searchParams.get(queryKey),
    defaultTab,
  );
  const activeTab = tabs.find((tab) => tab.key === activeKey) || tabs[0];
  const isMultiTab = tabs.length > 1;
  const hasPageTitle = !!(titleEn || titleCn);
  const showHeader = isMultiTab || hasPageTitle;

  useEffect(() => {
    if (!isMultiTab || !gameId || !activeTab) {
      return;
    }
    reportStudioTabLog(activeTab, currentGame?.game_name, gameId);
  }, [activeKey, gameId, isMultiTab, activeTab, currentGame?.game_name]);

  return (
    <Content className={classNames('dbh-studio-influencer', hasPageTitle && 'dbi-businessreports')}>
      {showHeader && (
        <StudioQueryTabs
          titleEn={titleEn}
          titleCn={titleCn}
          descriptionEn={descriptionEn}
          descriptionCn={descriptionCn}
          tabs={isMultiTab ? tabs.map((tab) => ({
            key: tab.key,
            labelEn: tab.nameEn,
            labelCn: tab.nameCn,
          })) : []}
          activeKey={activeKey}
          defaultActiveKey={defaultTab}
          queryKey={queryKey}
          info={activeTab?.info}
        />
      )}
      <Content.Body full className="dbh-studio-influencer-body">
        {activeTab && (
          <MarketingPageWrap key={activeTab.key}>
            <StudioTabPrNoticeMarquee
              tabKey={activeTab.key}
              prNoticeMarquee={activeTab.prNoticeMarquee}
            />
            <FilterGameInfoContext.Provider value={filterGameInfoContextValue}>
              {renderOpinionPage(activeTab)}
            </FilterGameInfoContext.Provider>
          </MarketingPageWrap>
        )}
      </Content.Body>
    </Content>
  );
};

export default InfluencerWrapper;
